import React, { useEffect, useState } from 'react'
import { Button, Form, Table } from 'react-bootstrap'
import { ReactComponent as ArrowLeft } from "../../images/arrow_left.svg"
import { useDataContext } from './CIContext';
import { UpdateCIConfigPayload } from './DataModels';
import displayNotification from '../../notification/displayNotification';
import { exceptionList } from './ExceptionList.data';
import { FetchCiConfig, UpdateCiConfig } from './CredibilityIndex.api';
import Logger from '../../utils/Logger';
import { NotificationType } from '../../notification/notifications.model';
import { CredibilityIndexAction } from './CredibilityIndex.action';
import { PageViewType } from './CredibilityIndex.constants';
import { toast } from "react-toastify"
import { handlePasteNumeric, isAnyToastVisible, preventInvalidNumberInput } from './ciUtils';

const BANDS = ['LOW', 'MEDIUM', 'HIGH'] as const;
type Band = typeof BANDS[number];
interface BandConfig { startValue: number; endValue: number }
interface BandItem { name: Band; config: BandConfig; }
interface EditedConfig {
    credibilityScoreBand: Partial<Record<Band, BandConfig>>;
    ciExceptionTypes: string[];
    exceptionTypeWithThreshold: Record<string, { enable: boolean; count: number | '' }>;
}

const OverallBand = () => {
    const context = useDataContext();
    const { state: {getCiConfigResponse, clientId, isLoading}, dispatch } = context!;

    const response = getCiConfigResponse;


    const { showNotification } = displayNotification()

    const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});
    const [editedConfig, setEditedConfig] = useState<EditedConfig>({
        credibilityScoreBand: {} as Partial<Record<Band, BandConfig>>,
        ciExceptionTypes: [],
        exceptionTypeWithThreshold: {}
    });

    useEffect(() => {
        return () => {
            if (isAnyToastVisible()) {
                toast.dismiss();
            }
        };
    }, [])

    useEffect(() => {
       if (!response) return;

       setEditedConfig(prev => ({
           ...prev,
           credibilityScoreBand: response.credibilityScoreBand ?? prev.credibilityScoreBand,
           ciExceptionTypes: response.exceptionAndEvents?.ciExceptionTypes ?? prev.ciExceptionTypes,
           exceptionTypeWithThreshold: response.exceptionAndEvents?.exceptionTypeWithThreshold ?? prev.exceptionTypeWithThreshold
       }));

   }, [response]);

    const onSwitchChange = (key: string, requiresInput?: boolean) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;

        setEditedConfig((prev: any) => {
            const exceptionTypeWithThreshold = { ...prev.exceptionTypeWithThreshold };
            let ciExceptionTypes = [...(prev.ciExceptionTypes || [])];

            if (requiresInput) {
                if (checked) {
                    exceptionTypeWithThreshold[key] = {
                        ...(exceptionTypeWithThreshold[key] ?? { enable: true, count: 0 }),
                        enable: true
                    };
                } else {
                    exceptionTypeWithThreshold[key] = {
                        enable: false,
                        count: 0
                    }
                }
            } else {
                if (checked && !ciExceptionTypes.includes(key)) {
                    ciExceptionTypes.push(key)
                };
                if (!checked) ciExceptionTypes = ciExceptionTypes.filter(k => k !== key);
            }
            return { ...prev, ciExceptionTypes, exceptionTypeWithThreshold };
        });
    };

    const onInputChange = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        const count = val ? Number(val) : '';
        setEditedConfig(prev => ({
            ...prev,
            exceptionTypeWithThreshold: {
                ...prev.exceptionTypeWithThreshold,
                [key]: {
                    ...(prev.exceptionTypeWithThreshold[key] ?? { enable: true, count: 0 }),
                    count,
                    enable: true
                }
            }
        }));
    };

    const handleBackButton = () => {
       
        const payload = {
            clientId: clientId
        }
        FetchCiConfig(payload).then((response: any) => {

            dispatch({
                type: CredibilityIndexAction.SET_CI_CONFIG_RESPONSE,
                payload: {
                    response: response,
                    activePage: PageViewType.TABLE_PAGE
                }
            });
        }).catch((error) => {
            Logger.error("Error:", error);
        })
        if (isAnyToastVisible()) {
            toast.dismiss()
        }
    };

     const hasFieldError = (band: Band, field: string) => {
        return validationErrors[band]?.includes(field) || false;
    };

    const updateConfigValue = (band: Band, field: 'startValue' | 'endValue', value: string) => {
        const numericValue = value === '' ? '' : (isNaN(Number(value)) ? value : Number(value));
        setEditedConfig((prev: any) => ({
            ...prev,
            credibilityScoreBand: {
                ...prev.credibilityScoreBand,
                [band]: {
                    ...getCiConfigResponse.credibilityScoreBand[band],
                    ...prev.credibilityScoreBand[band],
                    [field]: numericValue
                }
            }
        }));
    };

    let bands: BandItem[] = []
 
    const validateBandSequence = () => {
        const errors: Record<string, string[]> = {};
 
        bands = BANDS
            .map(band => {
                const config = editedConfig.credibilityScoreBand?.[band] || getCiConfigResponse.credibilityScoreBand[band];
 
                return {
                    name: band,
                    config: config
                };
            })

        const addError = (bandName: string, field: string) => {
      
            if (!errors[bandName]) {
                errors[bandName] = [];
            }
            if (!errors[bandName].includes(field)) {
                errors[bandName].push(field);
            }
        };
 
        let endValue = bands[0]?.config.endValue;
 
        for (let i = 1; i < bands.length; i++) {
            const currentBand = bands[i];
            const previousBand = bands[i - 1];
            const startValue = currentBand.config.startValue;

            if ((endValue + 1) !== startValue) {
                addError(previousBand.name, 'endValue');
                addError(currentBand.name, 'startValue');
                setValidationErrors(errors);
                return {
                    isValid: false,
                    error: "Band Values should be in sequence."
                };
            }

            if (startValue > currentBand.config.endValue) {
                addError(currentBand.name, 'startValue');
                setValidationErrors(errors);
                return {
                    isValid: false,
                    error: "Start value of a band must not be greater than end value."
                };
            }

            endValue = currentBand.config.endValue;

        }
        setValidationErrors(errors);
        return {
            isValid: true,
            error: null
        };
    };


    const handleSave = () => {

        const validation = validateBandSequence();

        if (!validation?.isValid) {
            showNotification(NotificationType.BAND_VALUE_VALIDATION_ERROR, validation.error!);
            return;
        }

        dispatch({ type: CredibilityIndexAction.IS_LOADING, payload: { isLoading: true } })

        const payload: UpdateCIConfigPayload = {
            clientId: clientId,
            flagConfiguration: null,
            credibilityScoreBand: {
                ...getCiConfigResponse?.credibilityScoreBand,
                ...editedConfig.credibilityScoreBand
            },
            exceptionAndEvents: {
                ciExceptionTypes: editedConfig.ciExceptionTypes,
                exceptionTypeWithThreshold: {
                    ...response.exceptionAndEvents?.exceptionTypeWithThreshold,
                    ...editedConfig.exceptionTypeWithThreshold
                }
            },
            publicReportShow: null
        }

        UpdateCiConfig(payload).then(() => {
            showNotification(NotificationType.BAND_UPDATE_SUCCESS, "Success: CI Overall values are saved");
            dispatch({ type: CredibilityIndexAction.IS_LOADING, payload: { isLoading: false } })
        }).catch((err) => {
            Logger.error("Error updating CI config", err);
            dispatch({ type: CredibilityIndexAction.IS_LOADING, payload: { isLoading: false } })
        });
    }


    return (
        <div>
            <div className='d-flex mt-4 mb-3'>
                <span className="c-pointer" onClick={handleBackButton}><ArrowLeft /></span>
                <h4 className='nav-padding'>{`Edit Credibility band configuration: "${clientId}"`}</h4>
            </div>

            <div className="d-flex">
                <Form.Control
                    type="text"
                    value="Overall"
                    className='cursor-not-allowed w-25 mr-3'
                    readOnly
                />
                <Form.Control
                    type="number"
                    value="1"
                    className='cursor-not-allowed w-25 mr-3'
                    readOnly
                />
                <Button size="sm" className="ml-2" onClick={handleSave} disabled={isLoading}>Save {isLoading && (
                    <span
                        className="spinner-border spinner-border-sm ml-2"
                        role="status"
                        aria-hidden="true"
                    />
                )}
                </Button>
            </div>
            <div className="mt-4">
                <Table bordered responsive className="text-nowrap">
                    <thead className="bg-sky-lighter">
                        <tr>
                            <th>Band</th>
                            <th>Start Value</th>
                            <th>End Value</th>
                        </tr>
                    </thead>
                    <tbody>
                        {getCiConfigResponse?.credibilityScoreBand &&
                            BANDS.map((band) => {
                                const bandConfig = editedConfig.credibilityScoreBand[band] || getCiConfigResponse?.credibilityScoreBand[band];
                                return (
                                    <tr key={band}>
                                        <td>{band}</td>
                                        <td>
                                            <Form.Control
                                                type="number"
                                                value={band === 'LOW' ? 0 : bandConfig.startValue}
                                                onChange={(e) => updateConfigValue(band, 'startValue', e.target.value)}
                                                className={`input-width ${hasFieldError(band, 'startValue') ? 'border-danger' : ''} ${band === 'LOW' ? 'cursor-not-allowed' : ''}`}
                                                disabled={band === 'LOW'}
                                                onKeyDown={preventInvalidNumberInput}
                                                onPaste={handlePasteNumeric}
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type={band === 'HIGH' ? 'text' : 'number'}
                                                value={band === 'HIGH' ? 'MAX' : bandConfig.endValue}
                                                onChange={(e) => updateConfigValue(band, 'endValue', e.target.value)}
                                                className={`input-width ${hasFieldError(band, 'endValue') ? 'border-danger' : ''} ${band === 'HIGH' ? 'cursor-not-allowed' : ''}`}
                                                disabled={band === 'HIGH'}
                                                onKeyDown={preventInvalidNumberInput}
                                                onPaste={handlePasteNumeric}
                                            />
                                        </td>
                                    </tr>
                                );
                            })
                        }
                    </tbody>
                </Table>
            </div>

            <div className='w-50'>
                <p className='font-weight-bold my-3'>Exception List for Low Credibility</p>
                <p><strong>Proctor Events</strong></p>
                {exceptionList.map((data) => (
                    <>
                        {data?.mainHeadingTitle && (
                            <div className='my-3'><strong>{data.mainHeadingTitle}</strong></div>
                        )}
                        <div key={data.key} className="d-flex justify-content-between align-items-center mb-3 ">
                            <div className="w-50 d-flex flex-column">
                                <div>{data.title}</div>
                            </div>
                            <div className="custom-control custom-switch">
                                <input
                                    type="checkbox"
                                    className="custom-control-input"
                                    id={`customSwitch-${data.key}`}
                                    checked={data.requiresInput
                                        ? !!editedConfig.exceptionTypeWithThreshold[data.key]?.enable
                                        : editedConfig.ciExceptionTypes.includes(data.key)}
                                    onChange={onSwitchChange(data.key, data.requiresInput)}
                                />
                                <label className="custom-control-label" htmlFor={`customSwitch-${data.key}`} />
                            </div>
                        </div>
                        {data.requiresInput && editedConfig.exceptionTypeWithThreshold[data.key]?.enable &&  (
                            <div className="mb-3" style={{ maxWidth: 200 }}>
                                <label className="small mb-1">{data.inputLabel}</label>
                                <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    value={editedConfig.exceptionTypeWithThreshold[data.key]?.count ?? ''}
                                    onChange={onInputChange(data.key)}
                                    placeholder="Enter value"
                                    onKeyDown={preventInvalidNumberInput}
                                    onPaste={handlePasteNumeric}
                                />
                            </div>
                        )}
                    </>
                ))}
            </div>
        </div>
    )
}

export default OverallBand